import { supabase } from '@/integrations/supabase/client';
import type { PersonaType } from '@/hooks/useUserPersona';

/**
 * One rule for "where does this person land?", used by sign-in (Auth), the marketing
 * home redirect, the header logo and signup.
 *
 * Habit note: Your Desk is the seller's daily trigger. Sellers and power traders always
 * land there — Desk Setup guides a brand-new seller to their first listing, and
 * Your Next Move™ takes over after graduation. Buyers land on Buy Requests — their
 * standing needs are their daily desk (Supplier Menus is one click away).
 *
 * Type-only import above on purpose: `useUserPersona` uses this module, so a runtime
 * import here would create a cycle.
 */

export const BUYER_LANDING_PATH = '/buy-requests';
export const SELLER_DAILY_PATH = '/desk';
/** Every brand-new signup (any plan, any persona) starts on Your Desk so Desk Setup delivers the first win.
 *  An invitation, share link or saved destination still wins over this default. */
export const FIRST_RUN_PATH = '/desk';

const LANDING_BY_PERSONA: Record<PersonaType, string> = {
  buyer: BUYER_LANDING_PATH,
  seller: SELLER_DAILY_PATH,
  buyer_seller: SELLER_DAILY_PATH,
};


/** Pages that are never a meaningful place to start after sign-up. */
const NEVER_RETURN_TO = /^\/(pricing|auth|onboarding)(\/|\?|$)/;

/**
 * A remembered return path must not override the first-run landing when it points at
 * a private supplier menu the person reached without an invitation, or at the plans /
 * sign-in pages. Invitation and share flows are handled separately.
 */
export function sanitizeFirstRunRedirect(
  path: string | null | undefined,
  hasPendingInvitation: boolean,
): string | null {
  if (!path || path === '/') return null;
  if (NEVER_RETURN_TO.test(path)) return null;
  if (!hasPendingInvitation && /^\/menu\//.test(path)) return null;
  return path;
}

export function resolveLandingPath(
  persona: PersonaType | null | undefined,
  /** Kept for callers; sellers land on Your Desk whether or not they have inventory yet. */
  _hasInventory?: boolean | null,
  /** Desk Setup finished? A buyer still setting up stays on Your Desk until it's done. */
  deskGraduated?: boolean | null,
): string {
  if (!persona) return SELLER_DAILY_PATH;
  if (persona === 'buyer' && deskGraduated === false) return FIRST_RUN_PATH;
  return LANDING_BY_PERSONA[persona];
}

/**
 * Has this person's company finished Desk Setup? Read from the one progress RPC
 * (get_desk_habit_progress). Null when unknown, so callers keep the persona default.
 */
export async function deskSetupGraduated(): Promise<boolean | null> {
  const { data, error } = await supabase.rpc('get_desk_habit_progress' as never, {} as never);
  if (error || !data) return null;
  const g = (data as { graduated?: boolean | null }).graduated;
  return typeof g === 'boolean' ? g : null;
}

/**
 * Cheap existence check: does the company have anything in inventory beyond drafts?
 * Returns null when it can't be determined, so callers fall back to the persona default.
 */
export async function companyHasInventory(
  companyId: string | null | undefined,
): Promise<boolean | null> {
  if (!companyId) return null;
  const { count, error } = await supabase
    .from('listings')
    .select('id', { count: 'exact', head: true })
    .eq('company_id', companyId)
    .neq('status', 'draft');
  if (error) {
    console.error('[landing] inventory check failed:', error);
    return null;
  }
  return (count ?? 0) > 0;
}
